import { useState } from 'react'
import { toast } from 'sonner'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { AddButton } from '@/components/common/AddButton'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  useNumberingSchemes,
  useDeactivateScheme,
  useResetSchemeCounter,
  useNumberingAssignments,
  useDeleteAssignment,
} from './hooks'
import { SchemeFormDialog } from './components/SchemeFormDialog'
import { AssignmentFormDialog } from './components/AssignmentFormDialog'
import type { NumberingScheme } from './types'
import { DOCUMENT_TYPE_LABELS, RESET_CYCLE_LABELS } from './types'

export function NumberingSchemesPage() {
  const confirm = useConfirm()
  const schemesQ = useNumberingSchemes()
  const assignmentsQ = useNumberingAssignments()
  const deactivate = useDeactivateScheme()
  const resetCounter = useResetSchemeCounter()
  const deleteAssignment = useDeleteAssignment()

  const [activeTab, setActiveTab] = useState<'schemes' | 'assignments'>('schemes')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<NumberingScheme | null>(null)
  const [creatingAssignment, setCreatingAssignment] = useState(false)

  const handleDeactivate = async (scheme: NumberingScheme) => {
    const ok = await confirm({
      title: `Dezaktywować schemat "${scheme.label}"?`,
      description: 'Schemat zostanie oznaczony jako nieaktywny.',
      confirmLabel: 'Dezaktywuj',
      variant: 'destructive',
    })
    if (ok) {
      deactivate.mutate(scheme.code, {
        onSuccess: () => toast.success('Schemat dezaktywowany.'),
        onError: () => toast.error('Błąd dezaktywacji.'),
      })
    }
  }

  const handleReset = async (scheme: NumberingScheme) => {
    const ok = await confirm({
      title: `Resetować licznik schematu "${scheme.label}"?`,
      description: `Reset licznika do ${scheme.counterStart}. Aktualny: ${scheme.currentCounter}. Następne numery zaczną od ${scheme.counterStart}.`,
      confirmLabel: 'Resetuj',
      variant: 'destructive',
    })
    if (ok) {
      resetCounter.mutate(scheme.code, {
        onSuccess: () => toast.success('Licznik zresetowany.'),
        onError: () => toast.error('Błąd resetu licznika.'),
      })
    }
  }

  const handleDeleteAssignment = async (id: number) => {
    const ok = await confirm({
      title: 'Usunąć przypisanie?',
      description: 'Dokumenty tego typu i produktu wrócą do schematu wildcard lub nie będą numerowane.',
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (ok) {
      deleteAssignment.mutate(id, {
        onSuccess: () => toast.success('Przypisanie usunięte.'),
        onError: () => toast.error('Błąd usuwania przypisania.'),
      })
    }
  }

  return (
    <div className="flex flex-col w-full h-full">
      <ListPageHeader
        title="Numeracja dokumentów"
        subtitle="Schematy numeracji i ich przypisania do typów dokumentów i produktów."
        actions={
          activeTab === 'schemes'
            ? <AddButton onClick={() => setCreating(true)}>Dodaj schemat</AddButton>
            : <AddButton onClick={() => setCreatingAssignment(true)}>Dodaj przypisanie</AddButton>
        }
      />

      <div className="border-b border-slate-200 px-4">
        <div className="flex gap-0">
          <TabButton active={activeTab === 'schemes'} onClick={() => setActiveTab('schemes')}>
            Schematy
          </TabButton>
          <TabButton active={activeTab === 'assignments'} onClick={() => setActiveTab('assignments')}>
            Przypisania
          </TabButton>
        </div>
      </div>

      <div className="p-4">
        {activeTab === 'schemes' && (
          <SchemesTab
            schemes={schemesQ.data ?? []}
            isLoading={schemesQ.isLoading}
            onEdit={setEditing}
            onDeactivate={handleDeactivate}
            onReset={handleReset}
          />
        )}
        {activeTab === 'assignments' && (
          <AssignmentsTab
            assignments={assignmentsQ.data ?? []}
            isLoading={assignmentsQ.isLoading}
            onDelete={handleDeleteAssignment}
          />
        )}
      </div>

      {(creating || editing) && (
        <SchemeFormDialog
          editing={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
        />
      )}

      {creatingAssignment && (
        <AssignmentFormDialog onClose={() => setCreatingAssignment(false)} />
      )}
    </div>
  )
}

function TabButton({ active, onClick, children }: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 transition-colors ${
        active
          ? 'border-sky-600 text-sky-700 font-medium'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  )
}

function SchemesTab({ schemes, isLoading, onEdit, onDeactivate, onReset }: {
  schemes: NumberingScheme[]
  isLoading: boolean
  onEdit: (s: NumberingScheme) => void
  onDeactivate: (s: NumberingScheme) => void
  onReset: (s: NumberingScheme) => void
}) {
  if (isLoading) return <p className="text-sm text-slate-500">Ładowanie schematów...</p>
  if (schemes.length === 0) return <p className="text-sm text-slate-500">Brak schematów. Utwórz pierwszy.</p>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Kod</th>
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Etykieta</th>
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Wzorzec</th>
            <th className="text-right py-2 px-3 text-xs uppercase text-slate-500 font-medium">Licznik</th>
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Reset</th>
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Status</th>
            <th className="py-2 px-3" />
          </tr>
        </thead>
        <tbody>
          {schemes.map(s => (
            <tr key={s.code} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-2 px-3 font-mono text-xs text-slate-700">{s.code}</td>
              <td className="py-2 px-3">{s.label}</td>
              <td className="py-2 px-3 font-mono text-xs text-sky-700">{s.pattern}</td>
              <td className="py-2 px-3 text-right font-mono">{s.currentCounter}</td>
              <td className="py-2 px-3 text-xs text-slate-500">{RESET_CYCLE_LABELS[s.resetCycle]}</td>
              <td className="py-2 px-3">
                <span className={`text-xs px-1.5 py-0.5 rounded ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {s.isActive ? 'Aktywny' : 'Nieaktywny'}
                </span>
              </td>
              <td className="py-2 px-3">
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => onEdit(s)} className="text-xs text-slate-600 hover:text-sky-700">Edytuj</button>
                  <button type="button" onClick={() => onReset(s)} className="text-xs text-amber-600 hover:text-amber-700">Resetuj licznik</button>
                  {s.isActive && (
                    <button type="button" onClick={() => onDeactivate(s)} className="text-xs text-red-600">Dezaktywuj</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AssignmentsTab({ assignments, isLoading, onDelete }: {
  assignments: import('./types').NumberingSchemeAssignment[]
  isLoading: boolean
  onDelete: (id: number) => void
}) {
  if (isLoading) return <p className="text-sm text-slate-500">Ładowanie przypisań...</p>
  if (assignments.length === 0) return <p className="text-sm text-slate-500">Brak przypisań.</p>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Typ dokumentu</th>
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Produkt</th>
            <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Schemat</th>
            <th className="py-2 px-3" />
          </tr>
        </thead>
        <tbody>
          {assignments.map(a => (
            <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-2 px-3">{DOCUMENT_TYPE_LABELS[a.documentType]}</td>
              <td className="py-2 px-3 font-mono text-xs">
                {a.configurationCode ?? <span className="text-slate-400 italic">wildcard (wszystkie)</span>}
              </td>
              <td className="py-2 px-3 font-mono text-xs text-sky-700">{a.schemeCode}</td>
              <td className="py-2 px-3 text-right">
                <button type="button" onClick={() => onDelete(a.id)} className="text-xs text-red-600 hover:text-red-700">Usuń</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
